/* Pre-order cart guard — pre-order and regular products never share a cart.
   Wunderwunsch's ERP imports an order before Ordersplit Pro splits it, so a pre-order item
   bought together with regular products lands in two ERP orders.

   Every add to cart is checked against /cart.js: fetch and XHR (also inside the Candy Rack
   frame), plain form posts to /cart/add, and /cart/update calls that add a variant. On a
   conflict a bottom sheet asks to switch the cart or keep it. After a switch a bar offers
   undo; the old cart sits in localStorage so the bar survives a reload.

   Markup, copy and the pre-order ID list: snippets/sh-preorder-guard.liquid.
   Preview: ?sh_po_guard_preview=preorder | regular | bar */
(() => {
  'use strict';

  const root = document.getElementById('sh-po-guard');
  const bar = document.getElementById('sh-po-guard-bar');
  const configEl = document.getElementById('sh-po-guard-config');
  if (!root || !bar || !configEl || window.__shPoGuard) return;

  let config;
  try { config = JSON.parse(configEl.textContent); }
  catch { return; }
  window.__shPoGuard = true;

  const copy = config.copy || {};

  const PREORDER = 'preorder';
  const REGULAR = 'regular';

  const ROUTES = (window.themeVariables && window.themeVariables.routes) || {};
  const ROOT = ROUTES.rootUrlWithoutSlash || '';
  const CART_BASE = ROUTES.cartUrl || '/cart';
  const URL_CART = CART_BASE + '.js';
  const URL_ADD = CART_BASE + '/add.js';
  const URL_UPDATE = CART_BASE + '/update.js';
  const URL_CLEAR = CART_BASE + '/clear.js';
  const ADD_PATH = /\/cart\/add(\.js|\.json)?$/;
  const UPDATE_PATH = /\/cart\/update(\.js|\.json)?$/;

  const STORE_KEY = 'sh-po-guard-undo';
  const BAR_MS = (Number(config.barSeconds) || 5) * 1000;

  // Requests made here skip the guard.
  const rawFetch = window.fetch.bind(window);

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // The top layer sits above every z-index: upsell popup, cart drawer, cookie banner, chat widget.
  const TOP_LAYER = typeof root.showPopover === 'function';

  // Showing again also moves it above whatever else is in the top layer.
  function raise(el) {
    if (!TOP_LAYER) {
      // All at the highest z-index, so the last one in the DOM wins.
      document.body.appendChild(el);
      return;
    }
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
  }

  function lower(el) {
    if (TOP_LAYER && el.matches(':popover-open')) el.hidePopover();
  }

  // Handled in assets/sh-atc-upsell-popup.js. A listener, so that A/B test can go without touching this file.
  const closeUpsell = () => document.dispatchEvent(new CustomEvent('sh-po-guard:close-upsell'));

  /* ===== Cart helpers ===== */

  function post(url, body) {
    return rawFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
  }

  async function getCart() {
    const response = await rawFetch(URL_CART, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Could not read the cart');
    return response.json();
  }

  async function refreshCart(sections) {
    const cart = await getCart();
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    if (sections) cart.sections = sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: false } }));
  }

  // Product info by variant id ({productId, title, preorder}), from sections/sh-preorder-item.liquid.
  // A product is pre-order when it uses the pre-order template.
  const INFO_KEY = 'sh-po-guard-products-v2';
  const lookups = new Map();
  let known = {};
  try { known = JSON.parse(window.sessionStorage.getItem(INFO_KEY)) || {}; }
  catch {}

  const page = config.page || {};
  if (page.productId) {
    (page.variantIds || []).forEach((id) => { known[id] = { productId: page.productId, title: page.title, preorder: !!page.preorder }; });
  }

  // /variants/<id> redirects to /products/<handle>?variant=<id> and drops any section_id, so learn the handle first.
  async function variantHandle(variantId) {
    const response = await rawFetch(`${ROOT}/variants/${variantId}`, { method: 'HEAD', credentials: 'same-origin' });
    const match = response.ok && new URL(response.url, window.location.href).pathname.match(/\/products\/([^/]+)/);
    return match ? match[1] : null;
  }

  async function productInfo(handle) {
    const response = await rawFetch(`${ROOT}/products/${handle}?section_id=sh-preorder-item`, { credentials: 'same-origin' });
    const html = response.ok ? await response.text() : '';
    const el = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sh-po-item]');
    const info = el ? JSON.parse(el.textContent) : null;
    return info && info.productId ? info : null;
  }

  // Cart items carry their product handle; anything else needs the variant redirect.
  function lookup(variantId, handle) {
    variantId = String(variantId);
    if (!lookups.has(variantId)) {
      lookups.set(variantId, Promise.resolve(handle || variantHandle(variantId))
        .then((productHandle) => (productHandle ? productInfo(productHandle) : null))
        .then((info) => {
          if (!info) return null;
          known[variantId] = { productId: info.productId, title: info.title, preorder: !!info.preorder };
          try { window.sessionStorage.setItem(INFO_KEY, JSON.stringify(known)); }
          catch {}
          return info;
        })
        .catch(() => null));
    }
    return lookups.get(variantId);
  }

  // A product that cannot be looked up counts as regular, so the add is never blocked by a failed lookup.
  async function variantType(variantId, handle) {
    const info = known[String(variantId)] || await lookup(variantId, handle);
    return info && info.preorder ? PREORDER : REGULAR;
  }

  const itemType = (item) => variantType(item.variant_id, item.handle);

  // Look up what is already in the cart now, so the first add does not wait for it.
  getCart().then((cart) => { (cart.items || []).forEach(itemType); }).catch(() => {});

  /* ===== Reading cart requests ===== */

  function cartAction(method, url) {
    if (String(method || 'GET').toUpperCase() !== 'POST' || !url) return null;
    let target;
    try { target = new URL(String(url), window.location.href); }
    catch { return null; }
    if (target.origin !== window.location.origin) return null;
    if (ADD_PATH.test(target.pathname)) return 'add';
    if (UPDATE_PATH.test(target.pathname)) return 'update';
    return null;
  }

  // FormData and URLSearchParams can come from the Candy Rack frame, so no instanceof.
  function parseBody(body) {
    if (typeof body === 'string') {
      const text = body.trim();
      if (text.charAt(0) !== '{') return { kind: 'query', data: new URLSearchParams(text) };
      try { return { kind: 'json', data: JSON.parse(text) }; }
      catch { return null; }
    }
    const tag = Object.prototype.toString.call(body);
    if (tag === '[object FormData]' || tag === '[object URLSearchParams]') return { kind: 'entries', data: body };
    return null;
  }

  function serialize(parsed) {
    if (parsed.kind === 'json') return JSON.stringify(parsed.data);
    if (parsed.kind === 'query') return parsed.data.toString();
    return parsed.data;
  }

  // Every variant the request would add, each with a way to take it back out of the request.
  function readLines(action, parsed) {
    const data = parsed.data;
    const lines = [];

    if (parsed.kind === 'json') {
      if (action === 'add' && Array.isArray(data.items)) {
        data.items.forEach((item) => {
          if (!item || item.id == null) return;
          lines.push({ id: String(item.id), drop: () => { data.items = data.items.filter((other) => other !== item); } });
        });
      } else if (action === 'add' && data.id != null) {
        lines.push({ id: String(data.id), drop: () => { delete data.id; } });
      } else if (action === 'update' && data.updates && !Array.isArray(data.updates)) {
        Object.keys(data.updates).forEach((key) => {
          if (/^\d+$/.test(key) && Number(data.updates[key]) > 0) {
            lines.push({ id: key, drop: () => { delete data.updates[key]; } });
          }
        });
      }
      return lines;
    }

    const keys = Array.from(new Set(Array.from(data.keys())));
    keys.forEach((key) => {
      let match;
      if (action === 'add' && key === 'id') {
        lines.push({ id: String(data.get(key)), drop: () => data.delete(key) });
      } else if (action === 'add' && (match = key.match(/^items\[(\d+)\]\[id\]$/))) {
        const prefix = `items[${match[1]}][`;
        lines.push({ id: String(data.get(key)), drop: () => keys.forEach((k) => { if (k.indexOf(prefix) === 0) data.delete(k); }) });
      } else if (action === 'update' && (match = key.match(/^updates\[(\d+)\]$/)) && Number(data.get(key)) > 0) {
        lines.push({ id: match[1], drop: () => data.delete(key) });
      }
    });
    return lines;
  }

  /* ===== Guard ===== */

  let queue = Promise.resolve();

  // One check at a time: a second add waits for the customer's answer and then sees the new cart.
  function guard(lines) {
    // Never block an add because the check itself failed.
    const result = queue.then(() => check(lines)).catch(() => ({ proceed: true }));
    queue = result;
    return result;
  }

  async function check(lines) {
    const [types, cart] = await Promise.all([Promise.all(lines.map((line) => variantType(line.id))), getCart()]);
    const items = cart.items || [];
    const itemTypes = await Promise.all(items.map(itemType));

    // A request with both kinds (product plus upsell) keeps the kind of its first product.
    const type = types[0];
    const incoming = lines.filter((line, i) => {
      if (types[i] === type) return true;
      line.drop();
      return false;
    });

    const others = items.filter((item, i) => itemTypes[i] !== type);
    if (!others.length) return { proceed: true };

    if ((await ask(type, others, incoming)) === 'switch') {
      try {
        return { proceed: true, saved: await switchCart(cart, others, type) };
      } catch {}
    }
    incoming.forEach((line) => line.drop());
    return { proceed: false };
  }

  // Takes the other kind out of the cart; returns what undo needs to put the old cart back.
  async function switchCart(cart, others, type) {
    const updates = {};
    others.forEach((item) => { updates[item.key] = 0; });
    const response = await post(URL_UPDATE, { updates });
    if (!response.ok) throw new Error('Could not switch the cart');
    return {
      removed: type === PREORDER ? REGULAR : PREORDER,
      items: cart.items.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        selling_plan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : undefined
      }))
    };
  }

  // The add that follows a switch: offer undo if it went through, otherwise put the old cart back.
  function afterAdd(saved, ok) {
    if (!ok) {
      restore(saved).catch(() => {});
      return;
    }
    saved.expires = Date.now() + BAR_MS;
    showBar(saved);
  }

  async function restore(saved) {
    await post(URL_CLEAR, {});
    const response = await post(URL_ADD, { items: saved.items, sections: ['mini-cart'] });
    let sections = null;
    if (response.ok) {
      sections = (await response.json()).sections;
    } else {
      // One sold-out item fails the whole request, so add the rest one by one.
      for (const item of saved.items) {
        await post(URL_ADD, { items: [item] }).catch(() => {});
      }
    }
    await refreshCart(sections);
  }

  // A kept cart answers the theme's add with a refusal; the theme would show it as a cart error.
  let quietErrorUntil = 0;
  function keptResponse(win) {
    quietErrorUntil = Date.now() + 2000;
    const body = JSON.stringify({ status: 422, message: 'Cart Error', description: '' });
    return new (win.Response || Response)(body, { status: 422, headers: { 'Content-Type': 'application/json' } });
  }

  window.addEventListener('cart-notification:show', (event) => {
    if (Date.now() > quietErrorUntil || !event.detail || event.detail.status !== 'error') return;
    quietErrorUntil = 0;
    event.stopImmediatePropagation();
  }, true);

  /* ===== Hooks: fetch, XHR, plain form posts, Candy Rack frame ===== */

  function patchFetch(win) {
    const nativeFetch = win.fetch;
    if (!nativeFetch) return;
    win.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && (input.url || input.href);
      const action = cartAction((init && init.method) || (input && input.method), url);
      const parsed = action && init ? parseBody(init.body) : null;
      const lines = parsed ? readLines(action, parsed) : [];
      if (!lines.length) return nativeFetch.apply(this, arguments);

      return guard(lines).then((result) => {
        if (!result.proceed) return keptResponse(win);
        const sent = nativeFetch.call(this, input, Object.assign({}, init, { body: serialize(parsed) }));
        if (result.saved) {
          sent.then((response) => afterAdd(result.saved, response.ok), () => afterAdd(result.saved, false));
        }
        return sent;
      });
    };
  }

  function patchXhr(win) {
    const proto = win.XMLHttpRequest && win.XMLHttpRequest.prototype;
    if (!proto) return;
    const open = proto.open;
    const send = proto.send;
    proto.open = function (method, url, async) {
      this.__shPoAction = async === false ? null : cartAction(method, url);
      return open.apply(this, arguments);
    };
    proto.send = function (body) {
      const parsed = this.__shPoAction ? parseBody(body) : null;
      const lines = parsed ? readLines(this.__shPoAction, parsed) : [];
      if (!lines.length) return send.apply(this, arguments);

      const xhr = this;
      guard(lines).then((result) => {
        if (result.saved) {
          xhr.addEventListener('loadend', () => afterAdd(result.saved, xhr.status >= 200 && xhr.status < 300));
        }
        // A kept cart still sends the request, emptied, so the caller gets its usual error callback.
        send.call(xhr, serialize(parsed));
      });
    };
  }

  function patchWindow(win) {
    if (!win || win.__shPoGuardPatched) return;
    win.__shPoGuardPatched = true;
    patchFetch(win);
    patchXhr(win);
  }

  // Candy Rack adds from a same-origin frame that it builds and rebuilds on its own.
  function bindCandyFrame() {
    const frame = document.getElementById('candyrack-frame');
    if (!frame || frame.__shPoGuardBound) return;
    frame.__shPoGuardBound = true;
    const bind = () => {
      try { patchWindow(frame.contentWindow); }
      catch {}
    };
    frame.addEventListener('load', bind);
    bind();
  }

  patchWindow(window);
  new MutationObserver(bindCandyFrame).observe(document.documentElement, { childList: true, subtree: true });
  bindCandyFrame();

  // Forms that post to /cart/add without JavaScript. Theme product forms prevent the post and use fetch.
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || form.__shPoChecked) return;
    if (cartAction(form.getAttribute('method'), form.action) !== 'add') return;
    const lines = readLines('add', { kind: 'entries', data: new FormData(form) });
    if (!lines.length) return;

    event.preventDefault();
    guard(lines).then((result) => {
      if (!result.proceed) return;
      if (result.saved) {
        result.saved.expires = Date.now() + BAR_MS;
        storeSaved(result.saved);
      }
      form.__shPoChecked = true;
      HTMLFormElement.prototype.submit.call(form);
    });
  });

  /* ===== Sheet ===== */

  const panel = root.querySelector('.sh-po-guard__panel');
  const textEl = root.querySelector('[data-sh-po-guard-text]');
  const cartEl = root.querySelector('[data-sh-po-guard-cart]');
  const addingRow = root.querySelector('[data-sh-po-guard-adding-row]');
  const addingEl = root.querySelector('[data-sh-po-guard-adding]');
  const stayLabel = root.querySelector('[data-sh-po-guard-stay-label]');
  const handle = root.querySelector('[data-sh-po-guard-handle]');

  let answer = null;
  let sheetId = 0;
  let lastFocus = null;
  let pageOverflow = '';

  function itemName(title, type, quantity) {
    let name = title || '';
    if (type === PREORDER && copy.preorderSuffix) name += ' ' + copy.preorderSuffix;
    return quantity > 1 ? `${quantity} × ${name}` : name;
  }

  // Two names, then "+ N weitere".
  function fillNames(el, names) {
    el.textContent = '';
    names.slice(0, 2).forEach((name) => {
      const line = document.createElement('span');
      line.textContent = name;
      el.appendChild(line);
    });
    if (names.length > 2) {
      const more = document.createElement('span');
      more.textContent = (copy.more || '').replace('[count]', names.length - 2);
      el.appendChild(more);
    }
  }

  // `type` is the kind being added; the cart holds the other kind.
  function present(type, cartNames) {
    const cartHoldsPreorder = type === REGULAR;
    textEl.textContent = cartHoldsPreorder ? copy.textPreorder : copy.textRegular;
    stayLabel.textContent = cartHoldsPreorder ? copy.stayPreorder : copy.stayRegular;
    fillNames(cartEl, cartNames);
    addingRow.hidden = false;
    addingEl.textContent = ' ';
    sheetId += 1;
    openSheet();
    return new Promise((resolve) => { answer = resolve; });
  }

  function ask(type, others, incoming) {
    const otherType = type === PREORDER ? REGULAR : PREORDER;
    const decision = present(type, others.map((item) => itemName(item.product_title, otherType, item.quantity)));
    const id = sheetId;
    Promise.all(incoming.map((line) => known[line.id] || lookup(line.id))).then((infos) => {
      if (id !== sheetId) return;
      const names = infos.filter(Boolean).map((info) => itemName(info.title, type, 1));
      if (names.length) fillNames(addingEl, names);
      else addingRow.hidden = true;
    });
    return decision;
  }

  function settle(choice) {
    if (!answer) return;
    const resolve = answer;
    answer = null;
    closeSheet();
    resolve(choice);
  }

  // Candy Rack's frame sits above everything; keep it out of sight while the sheet is open.
  function hideCandyFrame(hide) {
    const frame = document.getElementById('candyrack-frame');
    if (frame) frame.style.visibility = hide ? 'hidden' : '';
  }

  // An open theme drawer traps focus and closes on any click outside it, and the sheet is outside it.
  let pausedTraps = [];

  function pauseDrawers() {
    pausedTraps = Array.from(document.querySelectorAll('[open]'))
      .filter((el) => el.shouldTrapFocus)
      .map((el) => el.focusTrap);
    pausedTraps.forEach((trap) => trap.pause());
  }

  function resumeDrawers() {
    pausedTraps.forEach((trap) => trap.unpause());
    pausedTraps = [];
  }

  function openSheet() {
    if (root.hidden) {
      lastFocus = document.activeElement;
      pageOverflow = document.documentElement.style.overflow;
      pauseDrawers();
    }
    closeUpsell();
    hideCandyFrame(true);
    root.hidden = false;
    raise(root);
    document.documentElement.style.overflow = 'hidden';
    void root.offsetWidth;
    root.classList.add('is-open');
    panel.focus({ preventScroll: true });
  }

  function closeSheet() {
    root.classList.remove('is-open');
    panel.classList.remove('is-dragging');
    panel.style.transform = '';
    hideCandyFrame(false);
    const finish = () => {
      if (root.hidden || root.classList.contains('is-open')) return;
      lower(root);
      root.hidden = true;
      document.documentElement.style.overflow = pageOverflow;
      resumeDrawers();
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    };
    if (reducedMotion()) finish();
    else window.setTimeout(finish, 350);
  }

  function trapFocus(event) {
    const buttons = Array.from(panel.querySelectorAll('button:not([disabled])'));
    if (!buttons.length) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-sh-po-guard-switch]')) settle('switch');
    else if (event.target.closest('[data-sh-po-guard-stay]')) settle('stay');
  });

  document.addEventListener('keydown', (event) => {
    if (root.hidden || !answer) return;
    if (event.key === 'Escape') settle('stay');
    else if (event.key === 'Tab') trapFocus(event);
  });

  if (handle && window.PointerEvent) {
    let startY = 0;
    let dragging = false;
    handle.addEventListener('pointerdown', (event) => {
      if (window.innerWidth >= 1000) return;
      dragging = true;
      startY = event.clientY;
      handle.setPointerCapture(event.pointerId);
      panel.classList.add('is-dragging');
    });
    handle.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      panel.style.transform = `translateY(${Math.max(0, event.clientY - startY)}px)`;
    });
    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      panel.classList.remove('is-dragging');
      panel.style.transform = '';
      if (event.clientY - startY > 80) settle('stay');
    };
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);
  }

  /* ===== Undo bar ===== */

  const barMessage = bar.querySelector('[data-sh-po-bar-message]');
  const barTimer = bar.querySelector('[data-sh-po-bar-timer]');
  const undoButton = bar.querySelector('[data-sh-po-bar-undo]');

  let current = null;
  let remaining = 0;
  let runStart = 0;
  let hovering = false;
  let focused = false;
  let undoing = false;

  function readSaved() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY)); }
    catch { return null; }
  }

  function storeSaved(saved) {
    try {
      if (saved) window.localStorage.setItem(STORE_KEY, JSON.stringify(saved));
      else window.localStorage.removeItem(STORE_KEY);
    } catch {}
  }

  function showBar(saved) {
    current = saved;
    if (!saved.preview) storeSaved(saved);
    remaining = Math.max(0, saved.expires - Date.now());
    runStart = Date.now();
    barMessage.textContent = saved.removed === PREORDER ? copy.barPreorder : copy.barRegular;
    bar.style.setProperty('--sh-po-bar-from', String(Math.min(1, remaining / BAR_MS)));
    bar.style.setProperty('--sh-po-bar-ms', `${remaining}ms`);
    undoButton.disabled = false;
    bar.hidden = false;
    raise(bar);
    bar.classList.remove('is-running', 'is-paused');
    void bar.offsetWidth;
    bar.classList.add('is-visible', 'is-running');
    if (hovering || focused) pause();
  }

  function hideBar() {
    current = null;
    storeSaved(null);
    bar.classList.remove('is-visible');
    const finish = () => {
      if (current) return;
      lower(bar);
      bar.hidden = true;
      bar.classList.remove('is-running', 'is-paused');
    };
    if (reducedMotion()) finish();
    else window.setTimeout(finish, 350);
  }

  // The bar stays while it is touched, hovered or focused; the countdown picks up where it stopped.
  function pause() {
    if (!current || bar.classList.contains('is-paused')) return;
    remaining = Math.max(0, remaining - (Date.now() - runStart));
    bar.classList.add('is-paused');
  }

  function resume() {
    if (!current || undoing || hovering || focused || !bar.classList.contains('is-paused')) return;
    runStart = Date.now();
    current.expires = runStart + remaining;
    if (!current.preview) storeSaved(current);
    bar.classList.remove('is-paused');
  }

  bar.addEventListener('pointerenter', () => { hovering = true; pause(); });
  bar.addEventListener('pointerleave', () => { hovering = false; resume(); });
  bar.addEventListener('focusin', () => { focused = true; pause(); });
  bar.addEventListener('focusout', (event) => {
    if (bar.contains(event.relatedTarget)) return;
    focused = false;
    resume();
  });
  barTimer.addEventListener('animationend', () => {
    if (current && !undoing) hideBar();
  });

  undoButton.addEventListener('click', async () => {
    if (!current || undoing) return;
    const saved = current;
    undoing = true;
    pause();
    // The popup lists the product that undo takes back out.
    closeUpsell();
    undoButton.disabled = true;
    undoButton.setAttribute('aria-busy', 'true');
    try {
      if (!saved.preview) await restore(saved);
    } catch {}
    undoing = false;
    undoButton.removeAttribute('aria-busy');
    hideBar();
  });

  // The upsell popup moves itself to the end of <body> when it first opens, right after a switch.
  if (!TOP_LAYER) {
    document.addEventListener('variant:added', () => { if (current) raise(bar); });
  }

  const pending = readSaved();
  if (pending && pending.expires > Date.now()) showBar(pending);
  else storeSaved(null);

  /* ===== Preview ===== */

  const preview = new URLSearchParams(window.location.search).get('sh_po_guard_preview');
  if (preview === 'preorder' || preview === 'regular') {
    // preorder: a pre-order item is in the cart and a regular product is added.
    const adding = preview === 'preorder' ? REGULAR : PREORDER;
    const preorderName = itemName('Adventskalender personalisiert', PREORDER, 1);
    const regularName = itemName('TRIXIE Kinder Trinkflasche personalisiert', REGULAR, 1);
    window.setTimeout(() => {
      present(adding, [adding === REGULAR ? preorderName : regularName]);
      fillNames(addingEl, [adding === REGULAR ? regularName : preorderName]);
    }, 300);
  } else if (preview === 'bar') {
    showBar({ preview: true, removed: PREORDER, items: [], expires: Date.now() + BAR_MS });
  }
})();
